import React, { useState, useEffect, useRef } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Platform, View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import LoginScreen from './src/screens/LoginScreen';
import { HomeScreen } from './src/screens/HomeScreen';
import { CreateReportScreen } from './src/screens/CreateReportScreen';
import { AppManualScreen } from './src/screens/AppManualScreen';
import QrScannerScreen from './src/screens/QrScannerScreen';
import PwaInstallInstructionsScreen from './src/screens/PwaInstallInstructionsScreen';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { translations, Language } from './src/config/translations';
import { auth, firestore, restoreAuthSession } from './src/config/firebase';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';

const Stack = createNativeStackNavigator();

// PWA Detection Helper Function
function isPwa() {
  if (typeof window === 'undefined') {
      return false; // Not in a browser environment
  }
  // Check if running in standalone mode (most reliable check)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // Fallback for browsers that might use minimal-ui or fullscreen for PWAs
  if (["fullscreen", "minimal-ui"].some(
    (displayMode) => window.matchMedia('(display-mode: ' + displayMode + ')').matches
  )) {
    return true;
  }
  // Removed the non-standard navigator.standalone check
  /* 
  // @ts-ignore This property is non-standard
  if (window.navigator.standalone === true) {
      return true;
  }
  */
  return false;
}

// --- Function to attempt auto-login using stored code ---
const attemptAutoLogin = async (groupCode: string): Promise<boolean> => {
    console.log('Attempting auto-login with code:', groupCode);
    try {
        // 1. Ensure anonymous authentication
        let currentUser = auth.currentUser;
        if (!currentUser) {
            console.log('No user found, signing in anonymously for auto-login check...');
            const cred = await signInAnonymously(auth);
            currentUser = cred.user;
            if (!currentUser) throw new Error('anonymous_auth_failed');
            console.log('Anonymous sign-in successful for auto-login.');
        }
        const userId = currentUser.uid;

        // 2. Verify the group code
        console.log('Verifying group code in Firestore...');
        const groupsRef = collection(firestore, 'groups');
        const q = query(groupsRef, where('qrCode', '==', groupCode));
        const groupSnapshot = await getDocs(q);

        if (groupSnapshot.empty) {
            console.error('Auto-login failed: Invalid group code.');
            await AsyncStorage.removeItem('pendingGroupCode'); // Remove invalid code
            await AsyncStorage.removeItem('pendingGroupName'); 
            return false; // Indicate failure
        }

        const groupDoc = groupSnapshot.docs[0];
        const groupId = groupDoc.id;
        console.log('Group found for auto-login, ID:', groupId);
        
        // 3. Create user document (reuse function from LoginScreen if possible, simplified here)
        // This step might be redundant if restoreAuthSession handles it, but good to have
        // const userCreated = await createUserDocument(userId, groupId); // Assuming this function exists/is imported
        // if (!userCreated) throw new Error('user_creation_failed');

        // 4. Store necessary session info (restoreAuthSession might handle some of this)
        await AsyncStorage.setItem('currentGroupId', groupId);
        await AsyncStorage.setItem('currentQrCode', groupCode);
        await AsyncStorage.setItem('isLoggedIn', 'true');
        
        // 5. Clean up pending keys
        await AsyncStorage.removeItem('pendingGroupCode');
        await AsyncStorage.removeItem('pendingGroupName');
        console.log('Auto-login successful!');
        return true; // Indicate success

    } catch (error) {
        console.error('Error during auto-login attempt:', error);
        // Clean up keys even on error to prevent retries with bad data
        await AsyncStorage.removeItem('pendingGroupCode'); 
        await AsyncStorage.removeItem('pendingGroupName');
        return false; // Indicate failure
    }
};

export default function App() {
  const [language, setLanguage] = useState<Language>('nl');
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [checkedUrlParams, setCheckedUrlParams] = useState(false); // Flag to run param check only once

  useEffect(() => {
    const loadInitialData = async () => {
        // Load language first
        try {
            const savedLanguage = await AsyncStorage.getItem('selectedLanguage');
            if (!savedLanguage) {
                await AsyncStorage.setItem('selectedLanguage', 'nl');
                setLanguage('nl');
            } else {
                setLanguage(savedLanguage as Language);
            }
        } catch (error) {
            console.error('Error loading language:', error);
            setLanguage('nl');
        }

        // Check URL parameters only once on web
        if (Platform.OS === 'web' && !checkedUrlParams) {
            console.log('[Debug] Checking URL parameters...');
            const searchParams = new URLSearchParams(window.location.search);
            const groupCode = searchParams.get('groupCode');

            if (groupCode) {
                console.log(`[Debug] Found groupCode in URL: ${groupCode}`);
                try {
                    await AsyncStorage.setItem('pendingGroupCode', groupCode);
                    console.log('[Debug] Stored pendingGroupCode.');

                    // --- Attempt to fetch group name WITHOUT anonymous sign-in --- 
                    // Relying on Firestore rules allowing public read on groups collection
                    console.log(`[Debug] Querying Firestore for group name (public read) with qrCode: ${groupCode}`);
                    const groupsRef = collection(firestore, 'groups');
                    const q = query(groupsRef, where('qrCode', '==', groupCode));
                    const groupSnapshot = await getDocs(q);
                    console.log(`[Debug] Firestore query result (public): Found ${groupSnapshot.size} groups.`);

                    if (!groupSnapshot.empty) {
                        const groupData = groupSnapshot.docs[0].data();
                        console.log('[Debug] Group data (public):', groupData);
                        const groupName = groupData?.name;
                        if (groupName) {
                            console.log(`[Debug] Attempting to store pendingGroupName: ${groupName}`);
                            await AsyncStorage.setItem('pendingGroupName', groupName);
                            console.log('[Debug] Stored pendingGroupName successfully.');
                        } else {
                            console.warn('[Debug] Group found (public), but name field is missing or empty.', groupData);
                        }
                    } else {
                         console.warn('[Debug] No group found matching the qrCode in Firestore (public read).');
                    }
                    // --- End attempt to fetch group name --- 

                    window.history.replaceState({}, document.title, window.location.pathname);
                    console.log('[Debug] Cleaned groupCode from URL.');

                } catch (error) {
                    console.error('[Debug] Error processing groupCode from URL or fetching name:', error);
                    await AsyncStorage.removeItem('pendingGroupCode');
                    await AsyncStorage.removeItem('pendingGroupName');
                }
            } else {
                 console.log('[Debug] No groupCode found in URL.');
            }
            setCheckedUrlParams(true); 
        }
        
        // Initialize authentication state (this will handle anonymous login if needed later)
        await initializeAuth();
    };

    loadInitialData();

  }, [checkedUrlParams]); // Rerun if flag changes (should only be once)

  const initializeAuth = async () => {
      console.log('Initializing authentication...');
      setIsLoading(true); // Ensure loading is true until auth check AND auto-login attempt is done
      try {
          const unsubscribe = onAuthStateChanged(auth, async (user) => {
              console.log('Auth state changed. User:', user ? user.uid : null);
              let finalAuthStatus: boolean = false;
              if (user) {
                  const isValid = await restoreAuthSession();
                  console.log('Restored session valid:', isValid);
                  if(isValid) {
                      finalAuthStatus = true;
                      // Clear any pending codes if user is already validly logged in
                      await AsyncStorage.removeItem('pendingGroupCode'); 
                      await AsyncStorage.removeItem('pendingGroupName');
                  }
              }

              // If user is NOT authenticated after normal check, try auto-login
              if (!finalAuthStatus) {
                  const pendingCode = await AsyncStorage.getItem('pendingGroupCode');
                  if (pendingCode) {
                      console.log('No valid session, attempting auto-login with pending code...');
                      const autoLoginSuccess = await attemptAutoLogin(pendingCode);
                      if(autoLoginSuccess) {
                          finalAuthStatus = true; // Update status if auto-login worked
                      }
                  } else {
                      console.log('No valid session and no pending code found.');
                  }
              }
              
              setIsAuthenticated(finalAuthStatus);
              setIsLoading(false); // Set loading false AFTER auth check & auto-login attempt
              console.log('Auth initialization complete. Authenticated:', finalAuthStatus);
          });
          return () => unsubscribe();
      } catch (error) {
          console.error('Error initializing auth:', error);
          setIsAuthenticated(false); 
          setIsLoading(false);
      }
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#005575" />
      </View>
    );
  }

  if (Platform.OS === 'web' && !isPwa()) {
    return <PwaInstallInstructionsScreen />;
  }

  return (
    <NavigationContainer>
      <Stack.Navigator
        initialRouteName={isAuthenticated ? "Home" : "Login"}
        screenOptions={{
          headerStyle: {
            backgroundColor: '#005575',
          },
          headerTintColor: '#FFFFFF',
          headerTitleStyle: {
            fontWeight: '600',
          },
        }}
      >
        <Stack.Screen 
          name="Login" 
          component={LoginScreen}
          options={{ 
            headerShown: false 
          }}
        />
        <Stack.Screen 
          name="QrScanner" 
          component={QrScannerScreen}
          options={{ 
            title: 'Scan QR Code',
            headerShown: true
          }}
        />
        <Stack.Screen
          name="Home"
          component={HomeScreen}
          options={{
            headerShown: false,
            headerLeft: () => null,
          }}
        />
        <Stack.Screen
          name="CreateReport"
          component={CreateReportScreen}
          options={{
            title: translations[language]?.createReport?.title || 'New Report',
          }}
        />
        <Stack.Screen
          name="AppManual"
          component={AppManualScreen}
          options={{
            title: 'Handleiding',
          }}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
});
